import { useCallback, useEffect, useState } from 'react';
import type { RosterEntry } from '@nostos/shared';
import { getAdherence } from '../lib/api.js';
import { rosterRepository } from '../storage/index.js';

interface Row {
  entry: RosterEntry;
  count: number;
  last: string | null;
}

/** M1 read-back: for each local roster entry, pull its server adherence and show it by NAME. */
export function AdherencePanel({ refreshKey }: { refreshKey: number }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const roster = await rosterRepository.list();
      const result = await Promise.all(
        roster.map(async (entry) => {
          const events = await getAdherence(entry.assignmentId);
          // receivedAt is typed Date by Drizzle but arrives as an ISO string over REST.
          const first = events[0];
          return {
            entry,
            count: events.length,
            last: first ? String(first.receivedAt) : null,
          } satisfies Row;
        }),
      );
      // Newest activity first; never-active at the bottom.
      result.sort((a, b) => (b.last ?? '').localeCompare(a.last ?? ''));
      setRows(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900">Adherence</h2>
        <button onClick={load} className="text-xs font-medium text-blue-600" disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

      <ul className="mt-3 divide-y divide-slate-100">
        {rows.map(({ entry, count, last }) => (
          <li key={entry.id} className="flex items-center justify-between py-2 text-sm">
            <span className="font-medium text-slate-800">{entry.athleteName}</span>
            <span className="text-slate-500">
              {count} event{count === 1 ? '' : 's'}
              {last && <> · last {new Date(last).toLocaleString()}</>}
            </span>
          </li>
        ))}
        {rows.length === 0 && !loading && (
          <li className="py-2 text-sm text-slate-400">No assignments yet — assign one above.</li>
        )}
      </ul>
    </section>
  );
}
